import { queryOptions, useQuery } from '@tanstack/react-query';
import type { OnboardingAnswersResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';

/**
 * The current user's onboarding answers (`GET /v1/onboarding/answers`).
 * Per-user and shared across all the user's workspaces — the endpoint has no
 * account or workspace scope, so the key carries none (global-scope shape:
 * just the resource name). A user with no stored answers gets an empty
 * answers object, not an error.
 */

const STALE_TIME = 60_000;

export const onboardingAnswersQueryKey = ['onboarding-answers'] as const;

/** @public Returned as the API sends it (`{ answers: { listen_targets } }`). */
export async function getOnboardingAnswers(): Promise<OnboardingAnswersResponse> {
  return await hydraUiApi.onboarding.getAnswers();
}

/** @public */
export const getOnboardingAnswersQueryOptions = () =>
  queryOptions({
    queryKey: onboardingAnswersQueryKey,
    queryFn: getOnboardingAnswers,
    staleTime: STALE_TIME,
    // Readers fail open to "not answered"; the default 5xx retry backoff would
    // hold the onboarding redirect's full-page loader for seconds first.
    retry: false,
    // Several observers share this key across the onboarding screen and its
    // step bodies. One retrying an errored query on mount flips it back to
    // pending for all of them, re-shows the page loader, unmounts the
    // observer, and loops.
    retryOnMount: false,
  });

/** Fetches without suspending — callers fold `isLoading` into their own hold. */
export function useOnboardingAnswersOptional({
  queryConfig,
}: { queryConfig?: Omit<QueryConfig<typeof getOnboardingAnswersQueryOptions>, 'retryOnMount'> } = {}) {
  return useQuery({
    ...getOnboardingAnswersQueryOptions(),
    ...queryConfig,
    // Not overridable: see getOnboardingAnswersQueryOptions.
    retryOnMount: false,
  });
}
